import { createElement, lazy } from 'react';

import { RouteObject } from 'react-router-dom';

import { AuthGuard } from '@inbody/ui';

import { Urls } from './Urls';
import { DATA_VIEW_PAGES, type DataViewPageName } from './dataViewPages';

import { MenuAccessGuard } from '@/shared/components/menu-access-guard';

const MainPage = lazy(() =>
  import('@/pages/main').then((module) => ({ default: module.MainPage })),
);

const lazyDataViewPage = (name: DataViewPageName) =>
  lazy(() =>
    import('@/pages/data-view').then((module) => ({ default: module[name] })),
  );

const PermissionManagementPage = lazy(() =>
  import('@/pages/permission-management').then((module) => ({
    default: module.PermissionManagementPage,
  })),
);

const ApiCatalogPage = lazy(() =>
  import('@/pages/api-catalog').then((module) => ({
    default: module.ApiCatalogPage,
  })),
);

const InternalApiPage = lazy(() =>
  import('@/pages/internal-api').then((module) => ({
    default: module.InternalApiPage,
  })),
);

const ApiKeysPage = lazy(() =>
  import('@/pages/api-keys').then((module) => ({
    default: module.ApiKeysPage,
  })),
);

const MenuManagementPage = lazy(() =>
  import('@/pages/menu-management').then((module) => ({
    default: module.MenuManagementPage,
  })),
);

const ApiKeyOverviewPage = lazy(() =>
  import('@/pages/api-key-overview').then((module) => ({
    default: module.ApiKeyOverviewPage,
  })),
);

const UserPermissionPage = lazy(() =>
  import('@/pages/user-permission').then((module) => ({
    default: module.UserPermissionPage,
  })),
);

export const homePageRoute: RouteObject = {
  path: Urls.main.path,
  element: createElement(MainPage),
};

// 데이터 조회 페이지는 /me/menus 접근 권한을 MenuAccessGuard 로 검사한다.
const guardMenuAccess = (page: Parameters<typeof createElement>[0]) =>
  createElement(MenuAccessGuard, { children: createElement(page) });

// 데이터 조회 화면 라우트는 DATA_VIEW_PAGES 에서 만든다. lazy 는 모듈 로드 시 화면마다 한 번만 만들어진다.
export const dataViewPageRoutes: RouteObject[] = Object.values(
  DATA_VIEW_PAGES,
).map(({ path, page }) => ({
  path,
  element: guardMenuAccess(lazyDataViewPage(page)),
}));

export const permissionManagementPageRoute: RouteObject = {
  path: Urls.permissionManagement.path,
  element: createElement(AuthGuard, {
    roles: ['ADMIN'],
    children: createElement(PermissionManagementPage),
  }),
};

export const menuManagementPageRoute: RouteObject = {
  path: Urls.menuManagement.path,
  element: createElement(AuthGuard, {
    roles: ['ADMIN'],
    children: createElement(MenuManagementPage),
  }),
};

export const userPermissionPageRoute: RouteObject = {
  path: Urls.userPermission.path,
  element: createElement(AuthGuard, {
    roles: ['ADMIN'],
    children: createElement(UserPermissionPage),
  }),
};

export const apiKeyOverviewPageRoute: RouteObject = {
  path: Urls.apiKeyOverview.path,
  element: createElement(AuthGuard, {
    roles: ['ADMIN'],
    children: createElement(ApiKeyOverviewPage),
  }),
};

export const apiCatalogPageRoute: RouteObject = {
  path: `${Urls.apiCatalog.path}/:task?`,
  element: createElement(ApiCatalogPage),
};

// 사내 API 는 메뉴 권한이 아니라 이 시스템의 사용자(USER/ADMIN)면 누구나 본다(백엔드 정책과 같은 기준).
// /internal-api 는 첫 시스템으로 보낸다(페이지가 처리).
export const internalApiPageRoutes: RouteObject[] = [
  Urls.internalApi.path,
  `${Urls.internalApi.path}/:code/:task?`,
].map((path) => ({ path, element: createElement(InternalApiPage) }));

export const apiKeysPageRoute: RouteObject = {
  path: Urls.apiKeys.path,
  element: createElement(ApiKeysPage),
};

export const basePageRoute = [
  homePageRoute,
  ...dataViewPageRoutes,
  permissionManagementPageRoute,
  menuManagementPageRoute,
  userPermissionPageRoute,
  apiKeyOverviewPageRoute,
  apiCatalogPageRoute,
  ...internalApiPageRoutes,
  apiKeysPageRoute,
];
